import { isDefined } from 'react-financial-charts';
import type { ChartId, MoreProps } from '../types/commontypes';
import isObject from 'lodash-es/isObject';
import toPath from 'lodash-es/toPath';
import { getXValue } from '@react-financial-charts/core/lib/utils/ChartDataUtil';
import { ascending as d3Ascending } from 'd3-array';

export function getMouseXY(moreProps: { chartConfigs?: unknown; mouseXY?: unknown }, [ox, oy]: [number, number]) {
  if (Array.isArray(moreProps.mouseXY)) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    const [x, y] = moreProps.mouseXY as [number, number];
    const mouseXY = [x - ox, y - oy];
    return mouseXY;
  }
  return moreProps.mouseXY;
}
export function getMorePropsForChart(moreProps: { chartConfigs: unknown }, chartId: ChartId) {
  const { chartConfigs: chartConfigList } = moreProps;
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const chartConfig = (chartConfigList as Array<Record<string, unknown>>).find(each => each['id'] === chartId);
  if (chartConfig) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    const origin = chartConfig['origin'] as [number, number];
    const mouseXY = getMouseXY(moreProps, origin);
    return Object.assign(Object.assign({}, moreProps), {
      chartConfig,
      mouseXY,
    });
  }
  return undefined;
}
export function getSelected(interactives: unknown[]) {
  const selected = interactives
    .map((each: unknown) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      const e = each as { objects: Array<Record<string, unknown>> };
      const objects = e.objects.filter(obj => {
        return obj['selected'];
      });
      return Object.assign(Object.assign({}, e), { objects });
    })
    .filter((each: { objects: unknown[] }) => each.objects.length > 0);
  return selected;
}

/**
 * 드래그 중 임시 상태(override)에서 특정 필드값을 읽는 헬퍼.
 *
 * - `override.uuid`가 `uuid` 인자와 일치하고 해당 키가 정의돼 있으면 override 값 반환
 * - 그 외에는 `defaultValue`(영구 저장된 원본 값) 반환
 *
 * 도형 컴포넌트의 render에서 EachShape에 좌표 prop을 넘길 때 사용된다.
 * uuid 필드의 required/optional 여부는 호출 측 override 타입에 따른다.
 */
export function getValueFromOverride<T extends { uuid?: unknown }, K extends keyof T>(
  override: T | null | undefined,
  uuid: unknown,
  key: K,
  defaultValue: NonNullable<T[K]>,
): NonNullable<T[K]> {
  if (isDefined(override) && override.uuid === uuid && isDefined(override[key])) {
    return override[key];
  }
  return defaultValue;
}

export function getNewXY(moreProps: MoreProps) {
  const { xScale, chartConfig, xAccessor, plotData, mouseXY } = moreProps;
  const { yScale } = chartConfig as Record<string, unknown>;
  const mouseYArr = mouseXY as number[];
  const mouseY = mouseYArr[1];

  const x = getXValue(xScale, xAccessor, mouseXY, plotData ?? []);

  type ScaleFn = { domain: () => number[]; invert: (v: number) => number };
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const yScaleFn = yScale as ScaleFn;
  const [small, big] = yScaleFn.domain().slice().sort(d3Ascending);
  const y = yScaleFn.invert(mouseY);
  const newY = Math.min(Math.max(y, small), big);

  return [x, newY];
}

/**
 * Get the entries of an object with support for typed keys.
 * Object.entries() only returns existing key-value pairs, so values are never undefined.
 * @param obj
 * @returns Array of [key, value] tuples where values are guaranteed to be non-undefined
 */
export function entries<T extends Record<string, unknown>, K extends Extract<keyof T, string>>(
  obj: T,
): Array<[K, NonNullable<T[K]>]> {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return Object.entries(obj) as Array<[K, NonNullable<T[K]>]>;
}

/**
 * Get the keys of an object with support for typed keys.
 * @param obj
 * @returns
 */
export function keys<T extends Record<string, unknown>, K extends Extract<keyof T, string>>(obj: T): K[] {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return Object.keys(obj) as K[];
}

export function getShortSymbolName(fullName?: string): string {
  if (!fullName) return '';

  if (fullName === '') {
    return '';
  } else {
    return fullName.split('/')[0] + (fullName.split('-').length > 1 ? '-' + fullName.split('-')[1] : '');
  }
}

type PathSeg = string | number;
type Path = PathSeg[] | string;

export type BatchUpdateItem = { path: Path; value: unknown } | { path: Path; updater: (prev: unknown) => unknown };

type Indexable = Record<PropertyKey, unknown>;
const isIndexable = (v: unknown): v is Indexable => v !== null && (typeof v === 'object' || typeof v === 'function');

/**
 * 배치 set (중간 노드 1회 얕은 복사)
 *    복잡한 객체의 하위 여러 속성들을 반복 수정하되, React 용이므로 복사해야 하는 상황에서 사용.
 *    base 객체 전체 복사하는 것이 아니라 중간 경로에 있는 객체만 복사하도록 함.
 *    여러 업데이트에 의해서 중간 경로에 있는 객체가 여러번 반복 복제되지 않고 1회만 복제되도록 함.
 * @param base 대상 객체
 * @param updates 업데이트 배열
 * @returns 업데이트된 객체
 */
export function cloneWithBatchUpdate<T>(base: T, updates: BatchUpdateItem[]): T {
  if (!updates.length || !isObject(base)) return base;
  const clones = new WeakMap<object, unknown>();

  const ensureClone = (node: unknown) => {
    if (!isIndexable(node)) return node;
    const hit = clones.get(node);
    if (hit) return hit;
    // 배열/객체 모두 얕은 복사
    const copy = Array.isArray(node) ? node.slice() : { ...(node as object) };
    clones.set(node, copy);
    return copy;
  };

  const applyOne = (pathLike: Path, spec: Omit<BatchUpdateItem, 'path'>) => {
    const path = toPath(pathLike) as (string | number)[];
    const nodes: unknown[] = [base];
    const keys: (string | number)[] = [];

    // 1) 경로 따라 내려가되, 아직 복제는 안 함(지연)
    let curr: unknown = base;
    for (let i = 0; i < path.length; i++) {
      const k = path[i];
      keys.push(k);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      let next = isObject(curr) && (k as PropertyKey) in curr ? (curr as Indexable)[k as PropertyKey] : undefined;
      if (next === undefined) {
        // 다음 세그먼트가 숫자면 배열, 아니면 객체
        const nextIsIndex = typeof path[i + 1] === 'number';
        next = nextIsIndex ? [] : {};
      }
      nodes.push(next);
      curr = next;
    }

    // 2) 리프 계산
    const parent = nodes[nodes.length - 2];
    const leafKey = keys[keys.length - 1];
    const prevLeaf = isIndexable(parent) ? parent[leafKey] : undefined;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    const nextLeaf = 'value' in spec ? spec.value : (spec as { updater: (prev: unknown) => unknown }).updater(prevLeaf);

    // 3) 아래→위 복제-연결(각 노드 최대 1회 복제)
    let child = nextLeaf;
    for (let i = nodes.length - 2; i >= 0; i--) {
      const pOrig = nodes[i];
      const k = keys[i];
      const pClone = ensureClone(pOrig);
      if (k !== undefined && isIndexable(pClone)) {
        pClone[k] = child;
      }
      child = pClone;
    }
  };

  for (const u of updates) applyOne(u.path, u);
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return (clones.get(base) ?? base) as T;
}

/**
 * convert first character to uppercase
 * @param str
 * @returns
 */
export function ucFirst(str: string) {
  if (!str) return str; // return if empty string or null/undefined
  return str.charAt(0).toUpperCase() + str.slice(1);
}

//# sourceMappingURL=utils.js.map
